import React from 'react';
import { useColorContext } from '../../context/colorContext';
import { useLanguageContext } from '../../context/languageContext';
import BrandLogo from '../../components/ui/BrandLogo';

const toSafeHex = (hex, fallback) => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function BubbleLandingPage({
	current = 'home',
	onFeaturesClick,
	onPricingClick,
	onHelpClick,
	onCtaClick,
	onLogoClick,
	ctaLabel = 'Start now',
}) {
	const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent } = useColorContext();
	const { t } = useLanguageContext();

	const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0F');
	const safeBgSecondary = toSafeHex(bgSecondary, '#13131A');
	const safeTextPrimary = toSafeHex(textPrimary, '#FFFFFF');
	const safeTextSecondary = toSafeHex(textSecondary, '#A1A1AA');
	const safeAccent = toSafeHex(activeAccent, '#6366F1');

	const wrapperStyle = {
		position: 'fixed',
		top: 20,
		left: '50%',
		transform: 'translateX(-50%)',
		zIndex: 1000,
		width: 'calc(100% - 32px)',
		maxWidth: 1020,
		padding: '13px 20px',
		boxSizing: 'border-box',
		backgroundColor: safeBgSecondary,
		backgroundImage: `radial-gradient(circle at 10% 0%, ${hexToRgba(safeAccent, 0.11, safeAccent)} 0%, transparent 34%), linear-gradient(180deg, ${hexToRgba(safeAccent, 0.03, safeAccent)} 0%, transparent 28%, transparent 76%, ${hexToRgba(safeAccent, 0.03, safeAccent)} 100%)`,
		border: `1px solid ${hexToRgba(safeAccent, 0.18, safeAccent)}`,
		borderRadius: 20,
		boxShadow: `0 18px 50px ${hexToRgba(safeBgPrimary, 0.42, safeBgPrimary)}`,
	};

	const contentStyle = {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 20,
		maxWidth: 'min(1120px, 100%)',
		margin: '0 auto',
	};

	const logoStyle = {
		fontSize: '1.5rem',
		fontWeight: 700,
		background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${hexToRgba(safeAccent, 0.8, safeAccent)} 100%)`,
		WebkitBackgroundClip: 'text',
		WebkitTextFillColor: 'transparent',
		backgroundClip: 'text',
	};

	const navStyle = {
		display: 'flex',
		gap: 12,
		alignItems: 'center',
		flexWrap: 'wrap',
		justifyContent: 'center',
		flex: 1,
	};

	const navItemBaseStyle = {
		color: safeTextSecondary,
		textDecoration: 'none',
		fontSize: '0.9rem',
		fontWeight: 500,
		cursor: 'pointer',
		padding: '10px 14px',
		borderRadius: 999,
		background: hexToRgba(safeBgPrimary, 0.18, safeBgPrimary),
		border: `1px solid ${hexToRgba(safeAccent, 0.08, safeAccent)}`,
		transition: 'color 220ms cubic-bezier(0.22, 1, 0.36, 1), border-color 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), background-color 220ms cubic-bezier(0.22, 1, 0.36, 1)',
	};

	const activeNavStyle = {
		color: safeTextPrimary,
		background: hexToRgba(safeAccent, 0.12, safeAccent),
		border: `1px solid ${hexToRgba(safeAccent, 0.32, safeAccent)}`,
		boxShadow: `0 8px 20px ${hexToRgba(safeAccent, 0.14, safeAccent)}`,
	};

	const ctaWrapStyle = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	};

	const ctaStyle = {
		padding: '10px 18px',
		background: `linear-gradient(135deg, ${safeAccent} 0%, ${hexToRgba(safeAccent, 0.72, safeAccent)} 100%)`,
		border: `1px solid ${hexToRgba(safeAccent, 0.25, safeAccent)}`,
		borderRadius: 999,
		color: '#FFFFFF',
		fontSize: '0.85rem',
		fontWeight: 600,
		cursor: 'pointer',
		transition: 'box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), filter 220ms cubic-bezier(0.22, 1, 0.36, 1)',
		boxShadow: `0 10px 24px ${hexToRgba(safeAccent, 0.26, safeAccent)}`,
	};

	const handleNavHoverIn = (event) => {
		event.currentTarget.style.color = safeTextPrimary;
		event.currentTarget.style.backgroundColor = hexToRgba(safeAccent, 0.08, safeAccent);
		event.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.35, safeAccent);
		event.currentTarget.style.boxShadow = `0 8px 20px ${hexToRgba(safeAccent, 0.14, safeAccent)}`;
	};

	const handleNavHoverOut = (event) => {
		event.currentTarget.style.color = safeTextSecondary;
		event.currentTarget.style.backgroundColor = hexToRgba(safeBgPrimary, 0.18, safeBgPrimary);
		event.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.08, safeAccent);
		event.currentTarget.style.boxShadow = 'none';
	};

	const handleCtaHoverIn = (event) => {
		event.currentTarget.style.filter = 'brightness(1.06) saturate(1.03)';
		event.currentTarget.style.boxShadow = `0 14px 30px ${hexToRgba(safeAccent, 0.30, safeAccent)}`;
	};

	const handleCtaHoverOut = (event) => {
		event.currentTarget.style.filter = 'none';
		event.currentTarget.style.boxShadow = `0 10px 24px ${hexToRgba(safeAccent, 0.26, safeAccent)}`;
	};

	return (
		<header style={wrapperStyle} className="header-bubble">
			<style>{`
				.nav-bubble-links { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; justify-content: center; flex: 1; }
				.header-bubble { box-sizing: border-box; }
				@media (max-width: 640px) {
					.nav-bubble-links { display: none; }
					.header-bubble { width: calc(100% - 16px); padding: 8px 12px; border-radius: 14px; }
				}
			`}</style>
			<div style={contentStyle}>
				<div
					onClick={onLogoClick}
					style={{
						cursor: 'pointer',
						transition: 'opacity 220ms cubic-bezier(0.22, 1, 0.36, 1)',
						flexShrink: 0,
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.opacity = '0.8';
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.opacity = '1';
					}}
				>
					<BrandLogo size={20} />
				</div>
				<nav className="nav-bubble-links">
					<a
						style={current === 'features' ? { ...navItemBaseStyle, ...activeNavStyle } : navItemBaseStyle}
						onClick={onFeaturesClick}
						onMouseEnter={handleNavHoverIn}
						onMouseLeave={handleNavHoverOut}
					>
						{t('landing.navFeatures')}
					</a>

								<a
									style={current === 'help' ? { ...navItemBaseStyle, ...activeNavStyle } : navItemBaseStyle}
						onClick={onHelpClick}
						onMouseEnter={handleNavHoverIn}
						onMouseLeave={handleNavHoverOut}
					>
						{t('landing.navHelp')}
					</a>
				</nav>
				<div style={ctaWrapStyle}>
					<button
						type="button"
						style={ctaStyle}
						onClick={onCtaClick}
						onMouseEnter={handleCtaHoverIn}
						onMouseLeave={handleCtaHoverOut}
					>
						{ctaLabel}
					</button>
				</div>
			</div>
		</header>
	);
}
